
import {defineComponent} from 'vue'
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import BaseCard from "@/components/UI/Bootstrap/Card/BaseCard.vue";
import BaseCardHeader from "@/components/UI/Bootstrap/Card/BaseCardHeader.vue";
import BaseCardBody from "@/components/UI/Bootstrap/Card/BaseCardBody.vue";
import {UtilityHelper} from "@/helpers/UtilityHelper";
import {FullProductInfoRestDtoModel} from "@/models/api/pharma-pim-mgmt/FullProductInfoRestDtoModel";
import {PimMgmtRestService} from "@/services/rest/pim-mgmt/PimMgmtRestService";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {Tooltip} from "bootstrap";
import {useToast} from "vue-toastification";
import {PimFeedbackRestDtoModel} from "@/models/api/pharma-pim-mgmt/PimFeedbackRestDtoModel";
import PimFeedbackPanelOld from "@/components/layouts/pim/mgmt/PimFeedbackPanelOld.vue";
import MultilingualSwitcher from "@/components/UI/MultilingualSwitcher.vue";

export default defineComponent({
  name: "ProductSelectionProductsProductDetail",
  components: {
    BaseCardBody,
    BaseCardHeader,
    BaseCard,
    BaseSpinner,
    AlertError2,
    PimFeedbackPanelOld,
    MultilingualSwitcher,
  },
  props: {
    cnkCode: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      activeLanguage: 'nl' as string,
      languages: ['nl', 'fr', 'en', 'de'] as string[],

      productInfo: FullProductInfoRestDtoModel.createWithDefaults(),
      isShowFeedbackForFragmentWithId: null as string | null,
      isShowGeneralFeedback: false,

      toaster: useToast(),
      tooltips: [] as Tooltip[],

      productUI: UIStateDto.createWithDefaults()
    }
  },
  watch: {
    cnkCode() {
      this.reloadContent();
    },
    activeLanguage() {
      this.reloadPimContent();
    }
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    UtilityHelper() {
      return UtilityHelper
    },
  },
  methods: {
    async reloadContent(): Promise<void> {
      this.productUI
        .setNotReady()
        .clearError();

      try {
        await this.reloadPimContent();
      } catch (exceptions: unknown) {
        ErrorHandlerQueue
          .create()
          .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.productUI as UIStateDto))
          .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.productUI as UIStateDto))
          .catch(exceptions, true);
      } finally {
        this.productUI.setReady();
      }

    },
    async submitPimFeedback(feedback: PimFeedbackRestDtoModel): Promise<void> {
      this.productUI.clearError();

      try {
        await PimMgmtRestService.getInstance().createPimFeedback(this.cnkCode, this.activeLanguage, feedback);
        if (feedback.text_fragment_id != null) {
          this.toggleIsShowFeedbackForFragmentWithId(feedback.text_fragment_id);
        } else {
          this.isShowGeneralFeedback = false
        }

        await handleSavedSuccessfully();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      }
    },
    async submitPimFeedbackCancel(fragmentId: string | null): Promise<void> {
      this.isShowGeneralFeedback = false
      if (fragmentId !== null) {
        this.toggleIsShowFeedbackForFragmentWithId(fragmentId);
      }
    },
    toggleIsShowFeedbackForFragmentWithId(fragmentId: string): void {
      if (this.isShowFeedbackForFragmentWithId === fragmentId) {
        this.isShowFeedbackForFragmentWithId = null;
      } else {
        this.isShowFeedbackForFragmentWithId = fragmentId;
      }
    },
    initializeTooltips(): void {
      document.querySelectorAll('#product-selection-products-product-detail [data-bs-toggle="tooltip"]').forEach(el => {
        const tooltip = new Tooltip(el as Element);
        this.tooltips.push(tooltip);
      });
    },
    hideTooltips(): void {
      this.tooltips.forEach(tt => {
        tt.hide();
      });
    },
    async reloadPimContent(): Promise<void> {
      this.productInfo = await PimMgmtRestService.getInstance()
        .findMgmtProductInfo(this.cnkCode, this.activeLanguage, 'PUBLIC_WEBSITE');
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.productUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.productUI as UIStateDto))
        .catch(exceptions, true);
    }
  },
})
